/**
 * CardSourcesSection — surfaces the cards currently feeding live-API
 * transactions into this settlement. Each linked card can be detached
 * via × (cleans up its transactions in unlocked periods). A "+ Legg til
 * kort" picker lists cards that are shared with the household but not
 * yet linked. "Oppdater"-knappen kjører PSU-trigget refresh for alle.
 *
 * Live transactions themselves currently surface as a small summary
 * count + relative timestamp; the full transaction table can be added
 * later without touching this component's contract.
 */
import { Link } from "@tanstack/react-router";
import { useState } from "react";

import { t } from "../../../i18n";
import { formatRelative } from "../../../lib/format";
import { trpc } from "../../../lib/trpc";
import {
  Avatar,
  Badge,
  Button,
  Card,
  CardTitle,
  Dialog,
  Icon,
  IconButton,
  Money,
  Row,
  Spinner,
  useToast,
} from "../../ui";

type LinkedCardState = "active" | "renewal" | "expired";

function deriveCardState(card: {
  status: "created" | "active" | "expired" | "revoked";
  expiresAt: string;
  needsRenewal: boolean;
}): LinkedCardState {
  const expiresAtMs = new Date(card.expiresAt).getTime();
  if (card.status !== "active" || expiresAtMs < Date.now()) return "expired";
  if (card.needsRenewal) return "renewal";
  return "active";
}

interface CardSourcesSectionProps {
  settlementId: string;
  householdId: string;
  isLocked: boolean;
}

export function CardSourcesSection({
  settlementId,
  householdId,
  isLocked,
}: CardSourcesSectionProps) {
  const utils = trpc.useUtils();
  const { show } = useToast();

  const sources = trpc.settlement.listCardSources.useQuery({ settlementId });
  const liveTx = trpc.settlement.listLiveTransactions.useQuery({ settlementId });

  const [pickerOpen, setPickerOpen] = useState(false);

  const unlinkCard = trpc.settlement.unlinkCard.useMutation({
    onSuccess: () => {
      utils.settlement.listCardSources.invalidate({ settlementId });
      utils.settlement.listLiveTransactions.invalidate({ settlementId });
      // Card just freed up — it should reappear in the "+ Legg til kort" picker.
      utils.settlement.listAvailableCards.invalidate({ settlementId });
    },
    onError: (err) =>
      show({
        tone: "danger",
        title: t.cards.cardSources.unlinkFailed(err.message),
        dismissLabel: "OK",
      }),
  });

  const refresh = trpc.settlement.refresh.useMutation({
    onSuccess: (result) => {
      utils.settlement.listCardSources.invalidate({ settlementId });
      utils.settlement.listLiveTransactions.invalidate({ settlementId });
      // Sync just bumped last_synced_at on the connection — refresh Mine
      // kort so its "Synkronisert for …" label stays in sync with this view.
      utils.cardConnections.list.invalidate();
      // Refresh kan ha avdekket et nytt ekstrakort i settlement_expenses,
      // så Mine kort sin per-connection-listing må også oppdateres.
      utils.cardConnections.listCardsForConnection.invalidate();
      show({
        tone: "success",
        title: t.cards.cardSources.refreshSuccess(result.fetched),
        dismissLabel: "OK",
      });
    },
    onError: (err) =>
      show({
        tone: "danger",
        title: t.cards.cardSources.refreshFailed(err.message),
        dismissLabel: "OK",
      }),
  });

  const items = sources.data ?? [];
  const liveTxCount = liveTx.data?.length ?? 0;
  // Cards visible only when the section has any signal — sources OR pending.
  // Always rendered when not locked so the "+ Legg til kort" affordance is reachable.
  const hasContent = items.length > 0 || liveTxCount > 0 || !isLocked;
  if (!hasContent) return null;

  return (
    <>
      <Card padding="md">
        <Row align="center" gap={2} className="mb-3">
          <CardTitle className="flex items-center gap-2">
            <Icon name="credit-card" size="sm" className="text-ink-500" />
            {t.cards.cardSources.title}
            <Badge tone="neutral">{items.length}</Badge>
          </CardTitle>
          <div className="flex-1" />
          {items.length > 0 && !isLocked ? (
            <Button
              variant="ghost"
              size="sm"
              iconLeft="refresh-cw"
              isLoading={refresh.isPending}
              onClick={() => refresh.mutate({ settlementId })}
            >
              {t.cards.cardSources.refresh}
            </Button>
          ) : null}
        </Row>

        {items.length === 0 ? (
          <p className="mb-3 text-ink-500 text-sm">{t.cards.cardSources.empty}</p>
        ) : (
          <ul className="mb-3 divide-y divide-ink-100 rounded-md border border-ink-100">
            {items.map((src) => {
              const state = deriveCardState(src);
              // Sync-status: when the card has actually run a sync, show
              // "sist oppdatert X"; otherwise mirror Mine kort's "klar til
              // bruk" for active cards, and stay silent for renewal/expired
              // (their badge already explains why no transactions flow).
              const syncStatus = src.lastSyncedAt
                ? t.cards.cardSources.lastSync(formatRelative(src.lastSyncedAt))
                : state === "active"
                  ? t.cards.cardSources.readyToUse
                  : null;
              return (
                <li key={src.accountId} className="flex items-center gap-3 px-3 py-2.5 text-sm">
                  <Avatar name={src.aspspName} size="sm" />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="truncate font-medium text-ink-900">
                        {src.aspspName}
                        {src.mask ? <span className="text-ink-500"> · ••{src.mask}</span> : null}
                      </span>
                      {state === "renewal" && (
                        <Badge tone="warning" size="sm" iconLeft="alert-circle">
                          {t.cards.cardSources.needsRenewalLabel}
                        </Badge>
                      )}
                      {state === "expired" && (
                        <Badge tone="danger" size="sm" iconLeft="alert-circle">
                          {t.cards.cardSources.expiredLabel}
                        </Badge>
                      )}
                    </div>
                    <div className="truncate text-ink-500 text-xs">
                      {t.cards.cardSources.ownerLabel(src.ownerName ?? "")}
                      {syncStatus ? ` · ${syncStatus}` : null}
                    </div>
                    {state !== "active" && (
                      <Link
                        to="/profile/cards"
                        className="mt-1 inline-flex items-center gap-1 text-accent-700 text-xs hover:underline dark:text-accent-300"
                      >
                        {t.cards.cardSources.manageInMyCards}
                        <Icon name="arrow-right" size="xs" />
                      </Link>
                    )}
                  </div>
                  {!isLocked ? (
                    <IconButton
                      icon="x"
                      label={t.cards.cardSources.unlinkLabel(src.aspspName)}
                      onClick={() => unlinkCard.mutate({ settlementId, accountId: src.accountId })}
                      disabled={unlinkCard.isPending}
                    />
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}

        {liveTxCount > 0 ? (
          <p className="mb-3 text-ink-600 text-sm">
            {t.cards.cardSources.liveTxSummary(liveTxCount)}
          </p>
        ) : null}

        {!isLocked ? (
          <Button variant="secondary" onClick={() => setPickerOpen(true)}>
            <Icon name="plus" size="sm" />
            {t.cards.cardSources.addCard}
          </Button>
        ) : null}
      </Card>

      <AddCardDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        settlementId={settlementId}
        householdId={householdId}
      />
    </>
  );
}

function AddCardDialog({
  open,
  onClose,
  settlementId,
  householdId,
}: {
  open: boolean;
  onClose: () => void;
  settlementId: string;
  householdId: string;
}) {
  const utils = trpc.useUtils();
  const { show } = useToast();
  const [pendingAccountId, setPendingAccountId] = useState<string | null>(null);
  const available = trpc.settlement.listAvailableCards.useQuery(
    { settlementId },
    { enabled: open },
  );
  const linkCard = trpc.settlement.linkCard.useMutation({
    onSuccess: (result) => {
      utils.settlement.listCardSources.invalidate({ settlementId });
      utils.settlement.listAvailableCards.invalidate({ settlementId });
      utils.settlement.listLiveTransactions.invalidate({ settlementId });
      utils.cardConnections.list.invalidate();
      // Nye live-rader kan ha avdekket et ekstrakort under denne tilkoblingen,
      // så Mine kort må refetche per-connection-listingen.
      utils.cardConnections.listCardsForConnection.invalidate();
      setPendingAccountId(null);
      show({
        tone: "success",
        title: t.cards.cardSources.linkSuccess(result.fetched),
        dismissLabel: "OK",
      });
      onClose();
    },
    onError: (err) => {
      setPendingAccountId(null);
      show({
        tone: "danger",
        title: t.cards.cardSources.linkFailed(err.message),
        dismissLabel: "OK",
      });
    },
  });
  const shareCard = trpc.cardConnections.share.useMutation({
    onError: (err) => {
      setPendingAccountId(null);
      show({
        tone: "danger",
        title: t.cards.cardSources.shareFailed(err.message),
        dismissLabel: "OK",
      });
    },
  });

  const handleLink = (card: {
    accountId: string;
    connectionId: string;
    sharedWithHousehold: boolean;
  }) => {
    setPendingAccountId(card.accountId);
    if (card.sharedWithHousehold) {
      linkCard.mutate({ settlementId, accountId: card.accountId });
    } else {
      // Owner-only flow: share with the household first, then link to the
      // settlement. Both mutations invalidate cardConnections.list so Mine
      // kort reflects the new toggle immediately.
      shareCard.mutate(
        { connectionId: card.connectionId, groupId: householdId },
        {
          onSuccess: () => {
            linkCard.mutate({ settlementId, accountId: card.accountId });
          },
        },
      );
    }
  };

  const items = available.data ?? [];
  const isBusy = linkCard.isPending || shareCard.isPending;

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!isBusy) onClose();
      }}
      title={t.cards.cardSources.pickerTitle}
      description={t.cards.cardSources.pickerDescription}
      footer={
        <Button variant="ghost" onClick={onClose} disabled={isBusy}>
          {t.cards.cardSources.pickerClose}
        </Button>
      }
    >
      {available.isPending ? (
        <div className="flex justify-center py-6">
          <Spinner />
        </div>
      ) : items.length === 0 ? (
        <p className="py-2 text-ink-500 text-sm">{t.cards.cardSources.pickerEmpty}</p>
      ) : (
        <ul className="divide-y divide-ink-100 rounded-md border border-ink-100">
          {items.map((card) => {
            const isPending = pendingAccountId === card.accountId;
            return (
              <li key={card.accountId} className="flex items-center gap-3 px-3 py-2.5 text-sm">
                <Avatar name={card.aspspName} size="sm" />
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium text-ink-900">
                    {card.aspspName}
                    {card.mask ? <span className="text-ink-500"> · ••{card.mask}</span> : null}
                  </div>
                  <div className="truncate text-ink-500 text-xs">
                    {t.cards.cardSources.ownerLabel(card.ownerName ?? "")}
                    {!card.sharedWithHousehold && card.ownedByCaller && (
                      <span> · {t.cards.cardSources.pickerNotSharedHint}</span>
                    )}
                  </div>
                </div>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => handleLink(card)}
                  isLoading={isPending}
                  disabled={isBusy && !isPending}
                >
                  {card.sharedWithHousehold
                    ? t.cards.cardSources.linkAction
                    : t.cards.cardSources.linkActionShareAndAdd}
                </Button>
              </li>
            );
          })}
        </ul>
      )}
    </Dialog>
  );
}

// Ensure formatters used by the strings are imported (Money, etc.) — keep
// the import as a tree-shaken side-channel so future expansions don't have
// to re-add. Money currently unused at the summary level but expected
// once we render the live-tx list inline.
void Money;
